/**
 * MitarbeiterProfilModal — Vollständiges Profil-Bearbeitungs-Modal
 * 
 * Für Admins: Name, E-Mail, Telefon, Rolle, Betriebe, Berechtigungen (Modul-Toggles),
 * Passwort zurücksetzen/setzen, Account-Status, Profilbild
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Mail, Phone, Shield, Building2, Key, UserCheck, UserX, Save, Upload, RefreshCw, Eye, EyeOff, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamAdmin } from '@/hooks/useTeamAdmin';
import { ROLLEN_LABELS, ROLLEN_COLORS } from '@/lib/mitarbeiter';
import { toast } from 'sonner';
import type { TeamMember } from '@/contexts/AuthContext';

interface Props {
  member: TeamMember | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const ALLE_BETRIEBE = [
  { id: 'gutachten', label: 'Kfz Gutachten', kuerzel: 'GA' },
  { id: 'autoglas', label: 'Autoglas', kuerzel: 'AG' },
  { id: 'mietwagen', label: 'Mietwagen', kuerzel: 'MW' },
  { id: 'werkstatt', label: 'Werkstatt', kuerzel: 'WK' },
  { id: 'protex', label: 'Protex/Amazon', kuerzel: 'PX' },
  { id: 'unfallgiganten', label: 'Unfallgiganten', kuerzel: 'UG' },
  { id: 'airbnb', label: 'Airbnb/Immobilien', kuerzel: 'AB' },
  { id: 'recht', label: 'Recht & Forderungen', kuerzel: 'RF' },
];

// Module die in der Sidebar angezeigt werden — Admin kann pro Mitarbeiter ein/ausschalten
const SIDEBAR_MODULE = [
  { key: 'dashboard', label: 'Dashboard', beschreibung: 'Übersichts-Dashboard', defaultMitarbeiter: false },
  { key: 'kommunikation', label: 'Kommunikation', beschreibung: 'Interner Chat & Nachrichten', defaultMitarbeiter: true },
  { key: 'akten', label: 'Akten', beschreibung: 'Akten-Verwaltung & Prozesse', defaultMitarbeiter: false },
  { key: 'aufgaben', label: 'Aufgaben', beschreibung: 'Aufgaben & To-Do Listen', defaultMitarbeiter: true },
  { key: 'kalender', label: 'Kalender', beschreibung: 'Termine & Kalender', defaultMitarbeiter: false },
  { key: 'kontakte', label: 'Kontakte', beschreibung: 'Kontakte & Adressen', defaultMitarbeiter: false },
  { key: 'crm', label: 'CRM', beschreibung: 'Kundenbeziehungs-Management', defaultMitarbeiter: false },
  { key: 'marketing', label: 'Marketing', beschreibung: 'Marketing & Kampagnen', defaultMitarbeiter: false },
  { key: 'informationsschreiben', label: 'Informationsschreiben', beschreibung: 'Schreiben & Vorlagen', defaultMitarbeiter: false },
  { key: 'dokumente', label: 'Dokumente', beschreibung: 'Dokumenten-Verwaltung', defaultMitarbeiter: false },
  { key: 'finanzen', label: 'Finanzen', beschreibung: 'Rechnungen, Forderungen, Buchhaltung', defaultMitarbeiter: false },
  { key: 'einstellungen', label: 'Einstellungen', beschreibung: 'System-Einstellungen', defaultMitarbeiter: false },
];

const ROLLEN = ['admin', 'sachbearbeiter', 'mitarbeiter', 'kiosk'] as const;

// Avatar-Farben
const AVATAR_COLORS: Record<string, string> = {
  'AK': '#B91C1C', 'ID': '#2563EB', 'VM': '#059669',
  'TR': '#7C3AED', 'IH': '#D97706', 'AM': '#0D9488',
};

type TabId = 'profil' | 'berechtigungen' | 'sicherheit';

export default function MitarbeiterProfilModal({ member, open, onClose, onSaved }: Props) {
  const { isAdmin } = useAuth();
  const { updateMitarbeiter, sendPasswordReset, toggleAccountStatus, changeRole, updateBusinessUnits, updateModulePermissions, setPassword } = useTeamAdmin();
  
  const [activeTab, setActiveTab] = useState<TabId>('profil');
  const [saving, setSaving] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Formular-State
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [rolle, setRolle] = useState('');
  const [selectedBetriebe, setSelectedBetriebe] = useState<string[]>([]);
  
  // E-Mail-Signatur State
  const [emailSignatur, setEmailSignatur] = useState('');

  // Modul-Berechtigungen State
  const [moduleToggles, setModuleToggles] = useState<Record<string, boolean>>({});

  // Formular initialisieren wenn Member sich ändert
  useEffect(() => {
    if (member) {
      setVorname(member.vorname);
      setNachname(member.nachname);
      setEmail(member.email);
      setTelefon(member.telefon || '');
      setRolle(member.rolle);
      setSelectedBetriebe([...member.business_units]);
      setEmailSignatur((member as any).email_signatur || '');
      setActiveTab('profil');
      
      // Module-Permissions laden oder Defaults setzen
      const existingPerms = (member as any).module_permissions as Record<string, boolean> | null;
      if (existingPerms) {
        setModuleToggles(existingPerms);
      } else {
        // Defaults basierend auf Rolle
        const defaults: Record<string, boolean> = {};
        SIDEBAR_MODULE.forEach(m => {
          if (member.rolle === 'admin') {
            defaults[m.key] = true;
          } else {
            defaults[m.key] = m.defaultMitarbeiter;
          }
        });
        setModuleToggles(defaults);
      }
    }
  }, [member]);

  const handleSave = useCallback(async () => {
    if (!member || !isAdmin) return;
    setSaving(true);
    
    const success = await updateMitarbeiter(member.id, {
      vorname,
      nachname,
      telefon: telefon || undefined,
      rolle,
      business_units: selectedBetriebe,
      email_signatur: emailSignatur || undefined,
    });

    setSaving(false);
    if (success) {
      onSaved();
    }
  }, [member, isAdmin, vorname, nachname, telefon, rolle, selectedBetriebe, emailSignatur, updateMitarbeiter, onSaved]);

  const handleSavePermissions = useCallback(async () => {
    if (!member || !isAdmin) return;
    setSavingPermissions(true);
    
    const success = await updateModulePermissions(member.id, moduleToggles);
    
    setSavingPermissions(false);
    if (success) {
      onSaved();
    }
  }, [member, isAdmin, moduleToggles, updateModulePermissions, onSaved]);

  const handlePasswordReset = useCallback(async () => {
    if (!member?.email) return;
    await sendPasswordReset(member.email);
  }, [member, sendPasswordReset]);

  const handleToggleStatus = useCallback(async () => {
    if (!member) return;
    const success = await toggleAccountStatus(member.id, !member.ist_aktiv);
    if (success) onSaved();
  }, [member, toggleAccountStatus, onSaved]);

  const toggleBetrieb = (betriebId: string) => {
    setSelectedBetriebe(prev => 
      prev.includes(betriebId) 
        ? prev.filter(b => b !== betriebId)
        : [...prev, betriebId]
    );
  };

  const toggleModule = (moduleKey: string) => {
    setModuleToggles(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  const enableAllModules = () => {
    const all: Record<string, boolean> = {};
    SIDEBAR_MODULE.forEach(m => { all[m.key] = true; });
    setModuleToggles(all);
  };

  const setMinimalModules = () => {
    const minimal: Record<string, boolean> = {};
    SIDEBAR_MODULE.forEach(m => { minimal[m.key] = m.defaultMitarbeiter; });
    setModuleToggles(minimal);
  };

  if (!open || !member) return null;

  const initialen = `${member.vorname[0] || ''}${member.nachname[0] || ''}`.toUpperCase();
  const avatarColor = AVATAR_COLORS[initialen] || '#6B7280';
  const isAdminRole = rolle === 'admin';

  const tabs: { id: TabId; label: string; icon: any }[] = [
    { id: 'profil', label: 'Profil', icon: UserCheck },
    { id: 'berechtigungen', label: 'Module & Zugriff', icon: Shield },
    { id: 'sicherheit', label: 'Sicherheit', icon: Key },
  ];

  const activeModuleCount = Object.values(moduleToggles).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white w-full sm:h-auto rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
              style={{ backgroundColor: avatarColor }}
            >
              {initialen}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{member.vorname} {member.nachname}</h2>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLLEN_COLORS[member.rolle as keyof typeof ROLLEN_COLORS] || 'bg-gray-100 text-gray-600'}`}>
                  {ROLLEN_LABELS[member.rolle as keyof typeof ROLLEN_LABELS] || member.rolle}
                </span>
                {member.is_online ? (
                  <span className="text-[10px] font-medium text-green-600">Online</span>
                ) : (
                  <span className="text-[10px] text-gray-400">Offline</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#9B1C1C] text-[#9B1C1C]'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-6">
          {activeTab === 'profil' && (
            <div className="space-y-5">
              {/* Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Vorname</label>
                  <input
                    type="text"
                    value={vorname}
                    onChange={e => setVorname(e.target.value)}
                    disabled={!isAdmin}
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#9B1C1C] focus:ring-1 focus:ring-[#9B1C1C]/20 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Nachname</label>
                  <input
                    type="text"
                    value={nachname}
                    onChange={e => setNachname(e.target.value)}
                    disabled={!isAdmin}
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#9B1C1C] focus:ring-1 focus:ring-[#9B1C1C]/20 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>

              {/* Kontakt */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-Mail
                  </label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-500"
                  />
                  <p className="text-[9px] text-gray-400 mt-0.5">E-Mail kann nicht geändert werden (Auth-Account)</p>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Telefon
                  </label>
                  <input
                    type="tel"
                    value={telefon}
                    onChange={e => setTelefon(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="+49..."
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#9B1C1C] focus:ring-1 focus:ring-[#9B1C1C]/20 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>

              {/* Rolle */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Rolle
                </label>
                <div className="flex gap-2 mt-1.5">
                  {ROLLEN.map(r => (
                    <button
                      key={r}
                      onClick={() => isAdmin && setRolle(r)}
                      disabled={!isAdmin}
                      className={`text-[11px] font-medium px-3 py-1.5 rounded-lg border transition-all ${
                        rolle === r
                          ? 'border-[#9B1C1C] bg-red-50 text-[#9B1C1C] shadow-sm'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 disabled:opacity-50'
                      }`}
                    >
                      {ROLLEN_LABELS[r]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Betriebe */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Zugewiesene Betriebe
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                  {ALLE_BETRIEBE.map(b => (
                    <button
                      key={b.id}
                      onClick={() => isAdmin && toggleBetrieb(b.id)}
                      disabled={!isAdmin}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${
                        selectedBetriebe.includes(b.id)
                          ? 'border-[#9B1C1C]/30 bg-red-50/50 text-gray-800'
                          : 'border-gray-100 text-gray-400 hover:border-gray-200 disabled:opacity-50'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        selectedBetriebe.includes(b.id)
                          ? 'bg-[#9B1C1C] border-[#9B1C1C]'
                          : 'border-gray-300'
                      }`}>
                        {selectedBetriebe.includes(b.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-medium">{b.label}</span>
                        <span className="text-[9px] text-gray-400 ml-1">({b.kuerzel})</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* E-Mail-Signatur */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <Mail className="w-3 h-3" /> E-Mail-Signatur
                </label>
                <p className="text-[9px] text-gray-400 mt-0.5 mb-1.5">Diese Signatur wird automatisch an ausgehende E-Mails angehängt</p>
                <textarea
                  value={emailSignatur}
                  onChange={e => setEmailSignatur(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={`${member.vorname} ${member.nachname}\nGutachter Koop\nSachverständigenbüro für Kfz-Schäden\n\nTel: +49 (0) 40 - XXX XXX XX\nE-Mail: ${member.email}\nWeb: www.gutachter-koop.de`}
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#9B1C1C] focus:ring-1 focus:ring-[#9B1C1C]/20 outline-none disabled:bg-gray-50 disabled:text-gray-500 min-h-[120px] resize-none font-mono text-xs leading-relaxed"
                />
              </div>

              {/* Account-Status */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                <div>
                  <p className="text-xs font-medium text-gray-700">Account-Status</p>
                  <p className="text-[10px] text-gray-400">
                    {member.ist_aktiv ? 'Account ist aktiv und kann sich einloggen' : 'Account ist deaktiviert'}
                  </p>
                </div>
                <button
                  onClick={handleToggleStatus}
                  disabled={!isAdmin}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    member.ist_aktiv
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  } disabled:opacity-50`}
                >
                  {member.ist_aktiv ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                  {member.ist_aktiv ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'berechtigungen' && (
            <div className="space-y-4">
              {/* Info-Box */}
              {isAdminRole ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs font-medium text-green-700">
                    Administratoren haben automatisch Zugriff auf alle Module. Individuelle Einstellungen werden ignoriert.
                  </p>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-700">
                    Wähle aus, welche Module dieser Mitarbeiter in der Sidebar sehen kann. Änderungen werden sofort nach dem Speichern wirksam.
                  </p>
                </div>
              )}

              {/* Schnell-Aktionen */}
              {!isAdminRole && isAdmin && (
                <div className="flex gap-2">
                  <button
                    onClick={enableAllModules}
                    className="text-[10px] font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Alle aktivieren
                  </button>
                  <button
                    onClick={setMinimalModules}
                    className="text-[10px] font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Nur Standard (Aufgaben + Kommunikation)
                  </button>
                  <span className="text-[10px] text-gray-400 self-center ml-auto">
                    {activeModuleCount} von {SIDEBAR_MODULE.length} aktiv
                  </span>
                </div>
              )}

              {/* Modul-Toggles */}
              <div className="space-y-1">
                {SIDEBAR_MODULE.map(m => {
                  const isEnabled = isAdminRole ? true : (moduleToggles[m.key] ?? m.defaultMitarbeiter);
                  return (
                    <div
                      key={m.key}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                        isEnabled
                          ? 'border-green-200 bg-green-50/50'
                          : 'border-gray-100 bg-gray-50/30'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-semibold ${isEnabled ? 'text-gray-800' : 'text-gray-400'}`}>
                            {m.label}
                          </span>
                          {m.defaultMitarbeiter && (
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">
                              Standard
                            </span>
                          )}
                        </div>
                        <p className={`text-[10px] mt-0.5 ${isEnabled ? 'text-gray-500' : 'text-gray-300'}`}>
                          {m.beschreibung}
                        </p>
                      </div>
                      <button
                        onClick={() => !isAdminRole && isAdmin && toggleModule(m.key)}
                        disabled={isAdminRole || !isAdmin}
                        className="flex-shrink-0 ml-3 disabled:opacity-50"
                      >
                        {isEnabled ? (
                          <ToggleRight className="w-8 h-8 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-gray-300" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Betriebe-Zugriff */}
              <div className="mt-4">
                <h4 className="text-xs font-bold text-gray-600 mb-2">Betriebe-Zugriff</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {ALLE_BETRIEBE.map(b => {
                    const hasAccess = rolle === 'admin' || selectedBetriebe.includes(b.id);
                    return (
                      <div key={b.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                        hasAccess ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${hasAccess ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {b.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sicherheit' && (
            <div className="space-y-4">
              {/* Passwort zurücksetzen */}
              <div className="p-4 rounded-xl border border-gray-200 space-y-3">
                <div>
                  <h4 className="text-xs font-bold text-gray-700">Passwort zurücksetzen</h4>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Sendet eine E-Mail mit einem Link zum Zurücksetzen des Passworts an {member.email}
                  </p>
                </div>
                <button
                  onClick={handlePasswordReset}
                  disabled={!isAdmin}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Passwort-Reset-E-Mail senden
                </button>
              </div>

              {/* Login-Info */}
              <div className="p-4 rounded-xl border border-gray-200 space-y-2">
                <h4 className="text-xs font-bold text-gray-700">Login-Informationen</h4>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">E-Mail (Login)</span>
                    <span className="font-mono text-gray-700">{member.email}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Account erstellt</span>
                    <span className="text-gray-700">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString('de-DE') : 'Unbekannt'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Letzter Login</span>
                    <span className="text-gray-700">{member.last_seen || 'Noch nie'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Account-Status</span>
                    <span className={`font-medium ${member.ist_aktiv ? 'text-green-600' : 'text-red-600'}`}>
                      {member.ist_aktiv ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sicherheitshinweise */}
              <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
                <h4 className="text-xs font-bold text-blue-700 mb-1">Sicherheitshinweise</h4>
                <ul className="text-[10px] text-blue-600 space-y-1">
                  <li>Passwörter werden verschlüsselt in Supabase Auth gespeichert</li>
                  <li>Sessions laufen nach 7 Tagen Inaktivität automatisch ab</li>
                  <li>Alle Login-Versuche werden protokolliert</li>
                  <li>Deaktivierte Accounts können sich nicht einloggen</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isAdmin && (activeTab === 'profil' || activeTab === 'berechtigungen') && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              Abbrechen
            </button>
            {activeTab === 'profil' && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-[#9B1C1C] text-white hover:bg-[#7A1616] transition-colors disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Speichern...' : 'Änderungen speichern'}
              </button>
            )}
            {activeTab === 'berechtigungen' && !isAdminRole && (
              <button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-[#9B1C1C] text-white hover:bg-[#7A1616] transition-colors disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {savingPermissions ? 'Speichern...' : 'Berechtigungen speichern'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
